import { createSelector } from "reselect";

import { INdaData } from "../../types/ndaData.interface";
import { WORK_ORDER_TEMPLATE } from "../../types/templates/work-order/work-order-template.enum";

import {
  IWorkOrdersState,
  TWorkOrderResults,
  WorkOrdersState,
} from "../reducers/workOrdersReducer";
import { RootState } from "../store";

import { loadingSelector } from "./loading";

const getWorkOrderState = (state: RootState): WorkOrdersState => state.workOrders;

export const selectWorkOrder = createSelector(
  getWorkOrderState,
  (state: WorkOrdersState) => state.data
);

export const selectWorkOrderNdaData = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) =>
    ({ isUnderNda: data.isUnderNda, underNdaUserDetails: data.underNdaUserDetails } as INdaData)
);

export const selectWorkOrdersErrors = ({ workOrders }: IWorkOrdersState) => workOrders.errors;
export const selectWorkOrderFormErrors = ({ workOrders }: IWorkOrdersState) =>
  workOrders.formErrors;

export const selectWorkOrdersResultCount = ({ workOrders: { count } }: IWorkOrdersState) => count;
export const selectWorkOrdersResults = ({ workOrders: { results } }: IWorkOrdersState) => results;
export const selectWorkOrdersLoader = ({ workOrders }: IWorkOrdersState) => workOrders.loading;

export const selectWorkOrdersAssignees = ({
  workOrders: {
    filters: { assignees },
  },
}: IWorkOrdersState) => assignees;

export const selectWorkOrdersCreativePrimes = ({
  workOrders: {
    filters: { creativePrimes },
  },
}: IWorkOrdersState) => creativePrimes;

const loadingStateSelector = ({ workOrders: { loading } }: IWorkOrdersState) => loading;

export const isAddProductModalOpenWO = (state: IWorkOrdersState) =>
  state.workOrders.showAddProductModal;

export const workOrderBriefsLoadingSelector = loadingSelector(loadingStateSelector);

export const selectWorkOrderResourceType = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) => data.resourcetype as WORK_ORDER_TEMPLATE
);

export const selectWorkOrderName = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) => data.name
);

export const selectBreadcrumbInfo = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) => data.breadcrumbInfo ?? null
);

export const selectWorkOrderStatus = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) => data.status
);

export const selectWorkOrderId = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) => data.id
);

export const selectReviewers = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) => data.reviewers
);

export const selectExecutionBriefId = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) => data.executionBrief
);

export const selectWorkOrderOwnerId = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) => data.owner
);

export const selectWorkOrderIsBuyGet = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) => data.isBuyGet
);

export const selectWorkOrderContentCreatorDetails = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) => data.contentCreators.map((creator) => creator.userDetails)
);

export const selectHierarchyDetails = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) => data.hierarchyDetails
);

export const selectWorkOrderOwnerName = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) =>
    (data.ownerDetail &&
      data.ownerDetail.firstName &&
      data.ownerDetail.lastName &&
      `${data.ownerDetail.firstName} ${data.ownerDetail.lastName}`) ||
    "Someone"
);

export const selectWorkOrderUpdatedTime = createSelector(
  selectWorkOrder,
  (data: TWorkOrderResults) => data.updatedAt
);
